import { useParams, Link } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { articles } from '@/mocks/articles';
import { categoryMap } from '@/mocks/categories';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = useMemo(() => articles.find((a) => a.slug === slug), [slug]);
  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.category === article.category && a.slug !== article.slug)
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <h2 className="font-heading text-2xl text-foreground-900 mb-3">文章未找到</h2>
          <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm">返回首页</Link>
        </div>
      </div>
    );
  }

  const cat = categoryMap[article.category];
  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: cat?.name || article.category, to: `/category/${article.category}` },
    { label: article.title },
  ];

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    url: `https://hd-uncensored-guide.readdy.site/article/${article.slug}`,
    image: article.imageUrl,
    publisher: {
      '@type': 'Organization',
      name: '高清无码指南',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>

      <article className="pt-20 pb-14 md:pb-20 px-4 md:px-6 bg-background-50">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <div className="mb-6">
            <span className="inline-block px-2.5 py-1 rounded-full text-xs bg-primary-100 text-primary-700 mb-4 whitespace-nowrap">
              {cat?.name || article.category}
            </span>
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-time-line"></i>
                {article.readTime}
              </span>
            </div>
          </div>

          <img
            src={article.imageUrl}
            alt={article.title}
            title={`${article.title} - 高清无码视频编码技术`}
            className="w-full rounded-lg mb-8"
          />

          <div
            className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
              prose-h2:font-heading prose-h2:text-xl md:prose-h2:text-2xl prose-h2:text-foreground-900 prose-h2:mt-8 prose-h2:mb-4
              prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
              prose-strong:text-foreground-900"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-8 pt-6 border-t border-background-200/70">
            <div className="flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-block px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700 whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <section className="mt-14">
              <h3 className="font-heading text-xl text-foreground-900 mb-6">相关文章</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.slug} article={ra} />
                ))}
              </div>
            </section>
          )}
        </div>
      </article>
    </>
  );
}